import { Pencil } from 'lucide-react';
import { Button, Pill } from '@/shared/components';
import type { BookableResourceResponse } from '@/shared/api';
import { openingWindowLabel } from './resourceModel';
import { formatMoney } from '@/shared/lib/format';

/** Arbeitsliste: je Zeile Name, Buchungszeit, Zustand und genau eine Aktion. */
export function ResourceList({
  resources,
  canEdit,
  onEdit,
}: {
  resources: BookableResourceResponse[];
  canEdit: boolean;
  onEdit: (resource: BookableResourceResponse) => void;
}) {
  return (
    <ul className="divide-line/60 divide-y" aria-label="Ressourcen des Objekts">
      {resources.map((resource) => (
        <li
          key={resource.id}
          className="flex min-w-0 flex-wrap items-center gap-x-4 gap-y-2 px-5 py-3"
        >
          <div className="min-w-48 flex-1">
            <p
              className={
                resource.isActive
                  ? 'text-ink truncate font-serif text-lg'
                  : 'text-ink-soft truncate font-serif text-lg'
              }
              title={resource.name}
            >
              {resource.name}
            </p>
            <p className="text-ink-soft whitespace-nowrap text-sm tabular-nums">
              {openingWindowLabel(resource)} · {priceLabel(resource)}
            </p>
          </div>
          <Pill variant={resource.isActive ? 'live' : 'default'}>
            {resource.isActive ? 'Buchbar' : 'Deaktiviert'}
          </Pill>
          {resource.priceCurrencyMismatch && (
            <Pill
              variant="stop"
              title="Das Objekt führt inzwischen eine andere Währung. Bitte den Preis neu speichern."
            >
              Ungültige Währung
              <span className="sr-only">
                : Das Objekt führt inzwischen eine andere Währung. Bitte den Preis neu speichern.
              </span>
            </Pill>
          )}
          {canEdit && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="min-h-11"
              leftIcon={<Pencil size={14} aria-hidden />}
              aria-label={`${resource.name} bearbeiten`}
              onClick={() => onEdit(resource)}
            >
              Bearbeiten
            </Button>
          )}
        </li>
      ))}
    </ul>
  );
}

/** „Kostenlos“ oder der Brutto-Stundenpreis in der Währung, in der er gespeichert wurde. */
function priceLabel(resource: BookableResourceResponse): string {
  return resource.hourlyPrice > 0 && resource.priceCurrency
    ? `${formatMoney(resource.hourlyPrice, resource.priceCurrency)} je Stunde`
    : 'Kostenlos';
}
